import { usePiiLookup } from "@/api/pii-vault/use-pii-lookup";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useViewerIsSuperAdmin } from "@/lib/rights/use-viewer-is-baback-team";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import { Loader2, Search } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Navigate } from "react-router-dom";
import { PiiOrdersParcelsTable } from "./components/pii-orders-parcels-table";
import { PiiReturnRequestsTable } from "./components/pii-return-requests-table";
import { PiiSubjectCard } from "./components/pii-subject-card";

/**
 * Super-admin debugging page of the PII vault (RGPD-PII-VAULT-PLAN.md, step 1 bis).
 * Copy is hardcoded in French like the other Baback-internal pages.
 */
export default function PiiLookupPage() {
  const { data: shop } = useCurrentShopSuspense();
  const isSuperAdmin = useViewerIsSuperAdmin();
  const [email, setEmail] = useState("");
  const lookup = usePiiLookup(shop.id);

  if (!isSuperAdmin) {
    return <Navigate to={`/shops/${shop.id}`} replace />;
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    lookup.mutate(email.trim());
  };
  const result = lookup.data;
  const returnRequests =
    result?.documents.filter((d) => d.kind === "returnRequest") ?? [];
  const ordersAndParcels =
    result?.documents.filter((d) => d.kind !== "returnRequest") ?? [];

  return (
    <div className="p-6 space-y-6 max-w-5xl">
      <div>
        <h1 className="text-2xl font-semibold">Données personnelles</h1>
        <p className="text-sm text-muted-foreground">
          Compare ce que les commandes, retours et colis de {shop.name}{" "}
          contiennent en clair avec le coffre. Recherche sur l'email exact.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex gap-2">
        <Input
          type="email"
          required
          placeholder="client@exemple.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          className="max-w-md"
        />
        <Button type="submit" disabled={lookup.isPending}>
          {lookup.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Search className="w-4 h-4" />
          )}
          Rechercher
        </Button>
      </form>

      {result && (
        <div className="space-y-4" data-dd-privacy="mask">
          {!result.isVaultAvailable && (
            <p className="text-sm text-amber-700">
              Le coffre est désactivé ou indisponible sur cet environnement :
              seules les données en clair sont affichées.
            </p>
          )}
          <PiiSubjectCard
            subject={result.subject ?? null}
            addresses={result.addresses}
          />

          <div>
            <p className="font-medium mb-1">
              Retours ({returnRequests.length})
            </p>
            <PiiReturnRequestsTable shopId={shop.id} documents={returnRequests} />
          </div>

          <div>
            <p className="font-medium mb-1">
              Commandes et colis ({ordersAndParcels.length})
            </p>
            <PiiOrdersParcelsTable documents={ordersAndParcels} />
          </div>

          <p className="text-xs text-muted-foreground">
            {result.documentLimitPerKind} document(s) au plus par type.
          </p>
        </div>
      )}
    </div>
  );
}
